import { getApiData, postFormData } from "../utils/APIHelper";

const ENDPOINTS = {
  BOARD_BY_PROJECT: (projectId) => `api/inspirations/project/${projectId}`,
  BOARD: (id) => `api/inspirations/${id}`,
  CREATE_BOARD: () => `api/inspirations`,
  ADD_ITEMS: (boardId) => `api/inspirations/${boardId}/items`,
  ITEM: (itemId) => `api/inspiration-items/${itemId}`,
  COMMENTS_BY_ITEM: (itemId) => `api/inspiration-comments/item/${itemId}`,
  COMMENT: (id) => `api/inspiration-comments/${id}`,
  CREATE_COMMENT: () => `api/inspiration-comments`,

  // Design-comments / pins
  CREATE_PIN: () => `api/design-comments/pin`,
  PIN_REPLY: (pinId) => `api/design-comments/${pinId}/reply`,
  PINS_BY_REF: (refType, refId) => `api/design-comments/ref/${refType}/${refId}`,
  PIN_THREAD: (pinId) => `api/design-comments/thread/${pinId}`,
  COMMENT_BY_ID: (id) => `api/design-comments/${id}`,
  RESOLVE_PIN: (pinId) => `api/design-comments/${pinId}/resolve`,
  REOPEN_PIN: (pinId) => `api/design-comments/${pinId}/reopen`,
  PINS_BATCH: (refType, ids) =>
    `api/design-comments/batch/${refType}?ref_ids=${encodeURIComponent(ids.join(","))}`,
};

const safeCall = async (fn, label) => {
  try {
    return await fn();
  } catch (error) {
    console.error(`${label} error:`, error);
    throw error;
  }
};

// ---------- Board ----------

export const getInspirationByProject = (projectId) =>
  safeCall(
    () => getApiData(ENDPOINTS.BOARD_BY_PROJECT(projectId), "GET", {}),
    "Get inspiration"
  );

export const createInspiration = (projectId, brief = "", styleId = null) =>
  safeCall(() => {
    const body = { project_id: projectId };
    if (brief) body.brief = brief;
    if (styleId) body.style_id = styleId;
    return getApiData(ENDPOINTS.CREATE_BOARD(), "POST", body);
  }, "Create inspiration");

export const updateInspiration = (id, fields) =>
  safeCall(
    () => getApiData(ENDPOINTS.BOARD(id), "PATCH", fields),
    "Update inspiration"
  );

export const deleteInspiration = (id) =>
  safeCall(
    () => getApiData(ENDPOINTS.BOARD(id), "DELETE", {}),
    "Delete inspiration"
  );

// ---------- Items ----------

export const addInspirationFileItems = (boardId, category, type, files) =>
  safeCall(() => {
    const formData = new FormData();
    formData.append("data", JSON.stringify({ category, type }));
    Array.from(files).forEach((file) => formData.append("files", file));
    return postFormData(ENDPOINTS.ADD_ITEMS(boardId), formData);
  }, "Add inspiration file items");

export const addInspirationLink = (boardId, url, title) =>
  safeCall(() => {
    const body = {
      category: "moodboard",
      type: "link",
      url,
    };
    if (title) body.title = title;
    return getApiData(ENDPOINTS.ADD_ITEMS(boardId), "POST", body);
  }, "Add inspiration link");

export const deleteInspirationItem = (itemId) =>
  safeCall(
    () => getApiData(ENDPOINTS.ITEM(itemId), "DELETE", {}),
    "Delete inspiration item"
  );

// ---------- Comments (item level) ----------

export const getCommentsForItem = (itemId) =>
  safeCall(
    () => getApiData(ENDPOINTS.COMMENTS_BY_ITEM(itemId), "GET", {}),
    "Get item comments"
  );

export const addInspirationComment = (itemId, body, parentId, file) =>
  safeCall(() => {
    if (file) {
      const formData = new FormData();
      formData.append("item_id", itemId);
      formData.append("body", body);
      if (parentId) formData.append("parent_id", parentId);
      formData.append("file", file);
      return postFormData(ENDPOINTS.CREATE_COMMENT(), formData);
    }
    const payload = { item_id: itemId, body };
    if (parentId) payload.parent_id = parentId;
    return getApiData(ENDPOINTS.CREATE_COMMENT(), "POST", payload);
  }, "Add inspiration comment");

// ---------- Pins / Design comments ----------

export const getPinsForRef = (refType, refId) =>
  safeCall(
    () => getApiData(ENDPOINTS.PINS_BY_REF(refType, refId), "GET", {}),
    "Get pins"
  );

export const getPinsBatch = (refType, ids) => {
  if (!ids?.length) return Promise.resolve({ success: true, data: {} });
  return safeCall(
    () => getApiData(ENDPOINTS.PINS_BATCH(refType, ids), "GET", {}),
    "Batch pins"
  );
};

export const getPinThread = (pinId) =>
  safeCall(
    () => getApiData(ENDPOINTS.PIN_THREAD(pinId), "GET", {}),
    "Get pin thread"
  );

export const createPin = ({ refId, refType, x_pos, y_pos, body, files }) =>
  safeCall(() => {
    if (files && files.length) {
      const formData = new FormData();
      formData.append("ref_id", refId);
      formData.append("ref_type", refType);
      formData.append("x_pos", String(x_pos));
      formData.append("y_pos", String(y_pos));
      formData.append("body", body);
      Array.from(files).forEach((f) => formData.append("files", f));
      return postFormData(ENDPOINTS.CREATE_PIN(), formData);
    }
    return getApiData(ENDPOINTS.CREATE_PIN(), "POST", {
      ref_id: refId,
      ref_type: refType,
      x_pos,
      y_pos,
      body,
    });
  }, "Create pin");

export const replyToPin = (pinId, body, files) =>
  safeCall(() => {
    if (files && files.length) {
      const formData = new FormData();
      formData.append("body", body);
      Array.from(files).forEach((f) => formData.append("files", f));
      return postFormData(ENDPOINTS.PIN_REPLY(pinId), formData);
    }
    return getApiData(ENDPOINTS.PIN_REPLY(pinId), "POST", { body });
  }, "Reply to pin");

export const updateDesignComment = (id, body, files) =>
  safeCall(() => {
    if (files && files.length) {
      const formData = new FormData();
      formData.append("body", body);
      Array.from(files).forEach((f) => formData.append("files", f));
      return postFormData(ENDPOINTS.COMMENT_BY_ID(id), formData);
    }
    return getApiData(ENDPOINTS.COMMENT_BY_ID(id), "PATCH", { body });
  }, "Update comment");

export const deleteDesignComment = (id) =>
  safeCall(
    () => getApiData(ENDPOINTS.COMMENT_BY_ID(id), "DELETE", {}),
    "Delete comment"
  );

export const resolvePin = (pinId) =>
  safeCall(
    () => getApiData(ENDPOINTS.RESOLVE_PIN(pinId), "PATCH", {}),
    "Resolve pin"
  );

export const reopenPin = (pinId) =>
  safeCall(
    () => getApiData(ENDPOINTS.REOPEN_PIN(pinId), "PATCH", {}),
    "Reopen pin"
  );
